import type { ListScheduleEventsQuery } from '@sportninja/common/services/event/listScheduleEvents'

export interface BuildCompetitionEventsQueryArgs {
  /** `sn_team` — the team filter, NOT a `team_id` prop. See the note below. */
  teamId?: string | null
  venueId?: string | null
  facilityId?: string | null
  /**
   * The date window and sort the GAMES request is using for the selected
   * Past / Today / Upcoming tab. Passing them narrows the events request to
   * the same span, the way My Schedule does — see the note below.
   */
  startsAfter?: string | null
  startsBefore?: string | null
  order?: string | null
}

/**
 * Sized to fetch a competition's events in one round-trip. The service still
 * paginates past it, so this is a trip-count optimisation rather than a cap.
 */
const EVENTS_PER_PAGE = 200

/**
 * The filters for a competition's events, sent to `/schedules/{id}/events`.
 *
 * The schedule itself is the PATH, not a filter, so it is not built here — the
 * caller passes it to the service directly.
 *
 * Takes the SAME date window the games request sends for the selected period,
 * so the two lists are bounded identically — the arrangement My Schedule uses
 * (`useMembershipEventStream`), and the reason a competition with a year of history
 * no longer ships every event to render one tab.
 *
 * The window is part of the query and therefore part of the query key, so
 * switching period refetches on its own — no period-change effect needed.
 *
 * The window is OPTIONAL in the type, but every caller today passes one — the
 * list its selected period's. (The opening-tab decision no longer comes through
 * here at all: it asks for one row from today onward via
 * `resolveDecisionEventQuery`.)
 * Do NOT read "no window" as "no bound": the sibling `/events/membership`
 * endpoint answers an unwindowed request with an EMPTY list, so a caller that
 * omits the window is asking a materially different question than it looks.
 */
export const buildCompetitionEventsQuery = ({
  teamId,
  venueId,
  facilityId,
  startsAfter,
  startsBefore,
  order,
}: BuildCompetitionEventsQueryArgs): ListScheduleEventsQuery => {
  const query: ListScheduleEventsQuery = {
    per_page: EVENTS_PER_PAGE,
    // A competition- or season-level schedule has to reach the events hanging
    // off its children; without this a season would answer only for events
    // pinned to the season row itself, which is almost none of them.
    recursive: 1,
  }

  // Unlike the Team page — which scopes events to its `team_id` PROP — the
  // competition page has no team of its own, so the team FILTER is the only
  // team signal there is. Sending it keeps events narrowing in step with the
  // games list, which takes the same value.
  if (teamId) {
    query.team_id = teamId
  }
  if (venueId) {
    query.venue_id = venueId
  }
  if (facilityId) {
    query.facility_id = facilityId
  }
  // Only set when given: an absent key means "no bound", which is not the same
  // as sending an empty one. PAST is open-ended at the start and legitimately
  // has no `starts_after`.
  if (startsAfter) {
    query.starts_after = startsAfter
  }
  if (startsBefore) {
    query.starts_before = startsBefore
  }
  if (order) {
    query.order = order
  }

  return query
}
